import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IAverageConsumption } from "../../domain/IAverageConsumption";
import { ICreateChargeResponse } from "../../domain/ICreateChargeResponse";
import { ICreateDischargeResponse } from "../../domain/ICreateDischargeResponse";
import { ICreateServiceRequestParams } from "../../domain/ICreateServiceRequestParams";
import { IDeleteHistoricValueForResourceRequestParams } from "../../domain/IDeleteHistoricValueForResourceRequestParams";
import { IGetResourcesDetailsResponse } from "../../domain/IGetResourceDetailsResponse";
import { IInsepction } from "../../domain/IInspection";
import { InspectionType } from "../../domain/InspectionType";
import { IUpdateResource } from "../../domain/IUpdateResource";
import { resourcesServices } from "../../services/resource.service";
import { updateCharge } from "../Charges/charges.slice";
import { deleteRefuel } from "../FulesAndLubricants/fuelsAndLubricants.slice";
import { deleteServiceOrRepair } from "../ServiceAndRepair/serviceAndRepair.slice";

export const getResource = createAsyncThunk(
  "Resources/getResource",
  async (resourceId: number) => {
    try {
      return await resourcesServices.getResourceDetails(resourceId);
    } catch (err) {
      setError(err);
    }
  }
);

export const getResourcePicture = createAsyncThunk(
  "Resources/getResourcePicture",
  async (resourceId: number) => {
    try {
      return await resourcesServices.getResourcePicture(resourceId);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteHistoricValueForResource = createAsyncThunk(
  "Resources/deleteHistoricValueForResource",
  async (params: IDeleteHistoricValueForResourceRequestParams) => {
    try {
      return await resourcesServices.deleteHistoricValueForResource(params);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateResourceDetails = createAsyncThunk(
  "Resources/updateResourceDetails",
  async (params: IUpdateResource) => {
    try {
      var result = await resourcesServices.updateResource(params);
      return result;
    } catch (err) {
      setError(err);
    }
  }
);
interface detailsState {
  loading: boolean;
  resourceDetails: IGetResourcesDetailsResponse | undefined;
  resourcePicture: string | undefined;
  error: any;
}

const initialState: detailsState = {
  loading: false,
  resourceDetails: undefined,
  resourcePicture: "",
  error: null,
};

export const detailsSlice = createSlice({
  name: "resources-details",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateLastKnownUsageAmount: (state, action: PayloadAction<number>) => {
      if (state.resourceDetails !== undefined && state.resourceDetails.resource.lastKnownUsageAmount < action.payload) {
        state.resourceDetails.resource.lastKnownUsageAmount = action.payload;
      }
    },
    updateNextServiceRecommendedAt: (
      state,
      action: PayloadAction<ICreateServiceRequestParams>
    ) => {
      if (state.resourceDetails !== undefined) {
        if (action.payload.nextServiceRecommendedAtUsageAmount !== undefined) {
          state.resourceDetails.resource.nextServiceUsageAmount =
            action.payload.nextServiceRecommendedAtUsageAmount;
        }
        if (action.payload.nextServiceRecommendedAtDate !== undefined) {
          state.resourceDetails.resource.nextServiceDate =
            action.payload.nextServiceRecommendedAtDate;
        }
      }
    },
    updateNextInspectionDate: (state, action: PayloadAction<IInsepction>) => {
      if (state.resourceDetails !== undefined) {
        if (action.payload.type === InspectionType.REGISTRATION) {
          state.resourceDetails.resource.nextRegistrationDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.TACHOGRAPH) {
          state.resourceDetails.resource.nextTachographDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.DIGITAL_TACHOGRAPH) {
          state.resourceDetails.resource.nextTachographDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.FIRE_EXTINGUISHER) {
          state.resourceDetails.resource.nextFireExtinguishDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.TECHNICAL_INSPECTION) {
          state.resourceDetails.resource.nextTechnicalInspectionDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.FIRST_AID_KIT) {
          state.resourceDetails.resource.nextFirstAidDate =
            action.payload.nextInspectionDate;
        } else if (action.payload.type === InspectionType.FULL_COVERAGE_INSURANCE) {
          state.resourceDetails.resource.nextFullCoverageInsuranceDate =
            action.payload.nextInspectionDate;
        }
      }
    },
    updateResourceCharged: (state, action: PayloadAction<ICreateChargeResponse>) => {
      if (state.resourceDetails !== undefined) {
        state.resourceDetails.resource.charged = true;
        state.resourceDetails.resource.totalQuantityAmount = action.payload.totalQuantityAmount;
        state.resourceDetails.resource.remainingQuantityAmount = action.payload.remainingQuantityAmount;
        state.resourceDetails.resource.chargedQuantityAmount = action.payload.chargedQuantityAmount;
        state.resourceDetails.resource.chargeReportSequence = action.payload.charge.chargeReportSequence;
        state.resourceDetails.currentCharges = action.payload.currentCharges;
        state.resourceDetails.isRefuelAllowed = action.payload.isRefuelAllowed;
        state.resourceDetails.chargesSummary = action.payload.chargesSummary;
      }
    },
    updateResourceDischarged: (state, action: PayloadAction<ICreateDischargeResponse>) => {
      if (state.resourceDetails !== undefined) {
        state.resourceDetails.resource.charged = false;
        state.resourceDetails.resource.totalQuantityAmount = action.payload.totalQuantityAmount;
        state.resourceDetails.resource.remainingQuantityAmount = action.payload.remainingQuantityAmount;
        state.resourceDetails.resource.chargedQuantityAmount = action.payload.chargedQuantityAmount;
        state.resourceDetails.currentCharges = action.payload.currentCharges;
        state.resourceDetails.chargesSummary = action.payload.chargesSummary;
        state.resourceDetails.isRefuelAllowed = action.payload.isRefuelAllowed;
      }
    },
    updateHistoricValues: (state, action: PayloadAction<any>) => {
      if (action.payload.hasOwnProperty("resource")) {
        state.resourceDetails!.resource.historicValues =
          action.payload.resource.historicValues;
        state.resourceDetails!.resource.currentValue =
          action.payload.resource.currentValue;
      } else {
        let newHistoricValues = new Map([]);
        Object.entries(state.resourceDetails!.resource.historicValues).forEach(
          ([key, val]) => {
            if (key !== action.payload.year) {
              newHistoricValues.set(key, val);
            }
          }
        );
        newHistoricValues.set(action.payload.year, action.payload.value);
        const obj = Object.fromEntries(newHistoricValues);
        state.resourceDetails!.resource.historicValues = obj;
      }
    },
    updateAverageConsumptionOverride: (state, action: PayloadAction<number | null>) => {
      if (state.resourceDetails !== undefined) {
        if (action.payload === null) {
          state.resourceDetails.resource.averageConsumptionOverride = undefined;
        } else {
          state.resourceDetails.resource.averageConsumptionOverride = action.payload;
        }
      }
    },
    updateAverageConsumption: (state, action: PayloadAction<IAverageConsumption>) => {
      if (state.resourceDetails !== undefined) {
        state.resourceDetails.latestAverageConsumption = action.payload;
        state.resourceDetails.resource.averageConsumptionOverride = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getResource.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getResource.fulfilled, (state, action) => {
      state.loading = false;
      state.resourceDetails = action.payload;
    });
    builder.addCase(getResource.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getResourcePicture.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getResourcePicture.fulfilled, (state, action) => {
      state.loading = false;
      state.resourcePicture = action.payload;
    });
    builder.addCase(getResourcePicture.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateResourceDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.resourceDetails = action.payload;
    });
    builder.addCase(updateCharge.fulfilled, (state, action) => {
      state.loading = false;
      if (state.resourceDetails !== undefined) {
        state.resourceDetails.resource.remainingQuantityAmount = action.payload.resourceRemainingQuantityAmount;
        state.resourceDetails.resource.chargedQuantityAmount = action.payload.resourceChargedQuantityAmount;
        state.resourceDetails.chargesSummary = action.payload.chargesSummary;
      }
    });
    builder.addCase(deleteRefuel.fulfilled, (state, action) => {
      if (state.resourceDetails !== undefined) {
        state.resourceDetails = action.payload.resourceDetails;
      }
    });
    builder.addCase(deleteServiceOrRepair.fulfilled, (state, action) => {
      if (state.resourceDetails !== undefined) {
        state.resourceDetails = action.payload.resourceDetails;
      }
    });
  },
});

export const {
  setError,
  updateLastKnownUsageAmount,
  updateNextServiceRecommendedAt,
  updateNextInspectionDate,
  updateResourceCharged,
  updateResourceDischarged,
  updateHistoricValues,
  updateAverageConsumptionOverride,
  updateAverageConsumption,
} = detailsSlice.actions;

export const selectResourceDetails = (state: RootState) =>
  state.resources.details.resourceDetails;

export const selectState = (state: RootState) => state.resources.details;

export default detailsSlice.reducer;
